'use client';

import React from 'react';
import { Sparkles, X, RotateCcw } from 'lucide-react';
import { ENTRY_CONFIG, type AutoSwitchBanner as AutoSwitchBannerData } from './expense';
import type { ChildEntryType } from '../types';

export interface AutoSwitchBannerProps {
  banner: AutoSwitchBannerData;
  onRevert: (originalType: ChildEntryType) => void;
  onDismiss: () => void;
}

/**
 * Non-blocking banner shown when the receipt AI auto-switches the entry type.
 * Allows the user to revert to the original type or dismiss the suggestion.
 */
export function AutoSwitchBanner({ banner, onRevert, onDismiss }: AutoSwitchBannerProps) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-blue-300 bg-blue-50 px-4 py-3 dark:border-blue-700 dark:bg-blue-950/40">
      <Sparkles className="h-5 w-5 shrink-0 text-blue-500 dark:text-blue-400" />
      <div className="flex-1 text-sm">
        <span className="font-medium text-blue-900 dark:text-blue-200">
          AI detected: {banner.detectedLabel}
        </span>
        <span className="text-blue-700 dark:text-blue-400">
          {' '}(switched from {ENTRY_CONFIG[banner.originalType]?.label || banner.originalType})
        </span>
      </div>
      <button
        type="button"
        onClick={() => onRevert(banner.originalType)}
        className="inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100 dark:bg-blue-900/50 dark:text-blue-200 dark:hover:bg-blue-800/50"
      >
        <RotateCcw className="h-3 w-3" />
        Revert
      </button>
      <button
        type="button"
        onClick={onDismiss}
        className="inline-flex items-center justify-center rounded-lg p-1.5 text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/50"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
